import { useEffect, useState } from 'react';
import Icon from './Icon';
import { parseISODate, toISODate, today } from '../lib/dates';
import { LOCALE } from '../lib/format';
import { useApp } from '../lib/AppContext';

const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
const YEARS_PER_PAGE = 12;

// "tháng 10 năm 2026" -> "Tháng 10 năm 2026"
const monthTitle = (date) => {
  const text = date.toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' });
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const monthStart = (iso) => {
  const d = parseISODate(iso);
  return new Date(d.getFullYear(), d.getMonth(), 1);
};

const ARROW = 'btn btn-square btn-ghost btn-sm';
const CELL = 'rounded-field text-sm transition-colors disabled:text-muted disabled:opacity-40';

// The app's one date picker: a month grid starting on Monday. Tapping the title switches to months, then
// years, to jump far (a date of birth). Days outside `min`/`max` are disabled (Today passes max = today).
// `showLog` adds the log's marks: logged days get a dot and workout days are wrapped in a flame.
// `defaultMonth` is where an empty picker opens; `startView` can open it on 'years' instead.
export default function Calendar({ value, onSelect, min, max, showLog = false, defaultMonth, startView = 'days' }) {
  const { api } = useApp();
  const [month, setMonth] = useState(() => monthStart(value || defaultMonth || today()));
  const [view, setView] = useState(startView);
  const [logged, setLogged] = useState({});

  const year = month.getFullYear();
  const m = month.getMonth();
  const daysInMonth = new Date(year, m + 1, 0).getDate();
  const leadingBlanks = (month.getDay() + 6) % 7;
  const first = toISODate(month);
  const last = toISODate(new Date(year, m, daysInMonth));
  const [yearPage, setYearPage] = useState(() => year - (year % YEARS_PER_PAGE));

  useEffect(() => {
    if (!showLog) return undefined;
    let cancelled = false;
    api.get(`/stats?${new URLSearchParams({ from: first, to: last, group: 'day' })}`)
      .then((data) => {
        if (cancelled) return;
        setLogged(Object.fromEntries(data.series.filter((r) => r.logged).map((r) => [r.period, r])));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, first, last, showLog]);

  const todayIso = today();
  // Years and months are filled only for the chosen date; where an empty picker opened just gets a ring.
  const picked = value ? parseISODate(value) : null;
  const choiceClass = (isPicked, isHere) => (isPicked ? 'bg-neutral font-bold text-neutral-content'
    : `hover:bg-base-200 ${isHere ? 'ring-1 ring-base-300 font-semibold' : ''}`);
  const outside = (iso) => (min && iso < min) || (max && iso > max);
  // A month (or year) can be opened when any of its days can be picked.
  const monthOpen = (y, mo) => (!max || toISODate(new Date(y, mo, 1)) <= max) && (!min || toISODate(new Date(y, mo + 1, 0)) >= min);
  const yearOpen = (y) => (!min || y >= Number(min.slice(0, 4))) && (!max || y <= Number(max.slice(0, 4)));
  const shiftMonth = (delta) => setMonth(new Date(year, m + delta, 1));
  const prevMonth = new Date(year, m - 1, 1);
  const nextMonth = new Date(year, m + 1, 1);

  const header = (title, onTitle, onPrev, onNext, prevOk, nextOk, labels) => (
    <div className="flex items-center justify-between">
      <button type="button" className={ARROW} aria-label={labels[0]} disabled={!prevOk} onClick={onPrev}>
        <Icon name="chevronLeft" className="size-4" />
      </button>
      {onTitle ? (
        <button type="button" className="btn btn-ghost btn-sm gap-1 font-semibold" onClick={onTitle}>
          {title} <Icon name="chevronDown" className="size-3.5" />
        </button>
      ) : <span className="font-semibold">{title}</span>}
      <button type="button" className={ARROW} aria-label={labels[1]} disabled={!nextOk} onClick={onNext}>
        <Icon name="chevronRight" className="size-4" />
      </button>
    </div>
  );

  if (view === 'years') {
    const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPage + i);
    return (
      <div className="flex flex-col gap-2">
        {header(`${years[0]} – ${years[years.length - 1]}`, null,
          () => setYearPage(yearPage - YEARS_PER_PAGE), () => setYearPage(yearPage + YEARS_PER_PAGE),
          yearOpen(yearPage - 1), yearOpen(yearPage + YEARS_PER_PAGE), ['Các năm trước', 'Các năm sau'])}
        <div className="grid grid-cols-3 gap-1.5">
          {years.map((y) => (
            <button key={y} type="button" disabled={!yearOpen(y)} aria-pressed={picked?.getFullYear() === y}
              className={`${CELL} h-12 ${choiceClass(picked?.getFullYear() === y, y === year)}`}
              onClick={() => { setMonth(new Date(y, m, 1)); setView('months'); }}>
              {y}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (view === 'months') {
    return (
      <div className="flex flex-col gap-2">
        {header(`Năm ${year}`, () => { setYearPage(year - (year % YEARS_PER_PAGE)); setView('years'); },
          () => setMonth(new Date(year - 1, m, 1)), () => setMonth(new Date(year + 1, m, 1)),
          yearOpen(year - 1), yearOpen(year + 1), ['Năm trước', 'Năm sau'])}
        <div className="grid grid-cols-3 gap-1.5">
          {Array.from({ length: 12 }, (_, mo) => (
            <button key={mo} type="button" disabled={!monthOpen(year, mo)} aria-pressed={picked?.getFullYear() === year && picked?.getMonth() === mo}
              className={`${CELL} h-12 ${choiceClass(picked?.getFullYear() === year && picked?.getMonth() === mo, mo === m)}`}
              onClick={() => { setMonth(new Date(year, mo, 1)); setView('days'); }}>
              Tháng {mo + 1}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {header(monthTitle(month), () => setView('months'), () => shiftMonth(-1), () => shiftMonth(1),
        monthOpen(prevMonth.getFullYear(), prevMonth.getMonth()), monthOpen(nextMonth.getFullYear(), nextMonth.getMonth()),
        ['Tháng trước', 'Tháng sau'])}

      <div className="grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((d) => (
          <span key={d} className="py-1 text-xs font-medium text-muted">{d}</span>
        ))}
        {Array.from({ length: leadingBlanks }, (_, i) => <span key={`blank-${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const iso = toISODate(new Date(year, m, i + 1));
          const selected = iso === value;
          const isToday = iso === todayIso;
          const kcal = logged[iso]?.calories_in;
          const workout = logged[iso]?.workout_day;
          return (
            <button key={iso} type="button" onClick={() => onSelect(iso)} disabled={!!outside(iso)}
              aria-label={`${parseISODate(iso).toLocaleDateString(LOCALE)}${kcal != null ? `, đã ghi ${kcal} kcal` : ''}${workout ? ', ngày tập' : ''}`}
              aria-pressed={selected}
              className={`relative flex aspect-square flex-col items-center justify-center ${CELL}
                ${selected ? 'bg-neutral font-bold text-neutral-content' : 'hover:bg-base-200'}
                ${isToday && !selected ? 'font-bold text-brand-text ring-1 ring-primary' : ''}
                ${workout && !selected ? 'bg-train-bg font-semibold text-train' : ''}`}>
              {workout && (
                <Icon name="fire"
                  className={`absolute inset-x-0 top-0.5 mx-auto size-[72%] ${selected ? 'text-[#c4b5fd]' : 'text-[#8e72d8]'}`} />
              )}
              <span className="relative">{i + 1}</span>
              {kcal != null && (
                <span className={`absolute bottom-1 size-1.5 rounded-full ${selected ? 'bg-[#6fd3a0]' : 'bg-primary'}`} />
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-1 text-xs text-muted">
        {showLog ? (
          <span className="flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-primary" /> Có ghi chép
            <Icon name="fire" className="ml-2 size-3.5 text-[#8e72d8]" /> Ngày tập
          </span>
        ) : <span />}
        {!outside(todayIso) && (
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => onSelect(todayIso)}>
            {showLog ? 'Về hôm nay' : 'Hôm nay'}
          </button>
        )}
      </div>
    </div>
  );
}
